import { Component, ViewChild, OnDestroy, ViewEncapsulation, OnInit } from '@angular/core';
import { RowDragEvent, RowNode, SelectionChangedEvent, CellClickedEvent } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, HelpIndex, MessageType, UpdateStatusType, JobPriorityMode } from '../../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, FilterTemplateType, GenericColDef, GridContext, GridRowSelectionType } from '../../../models/grid-context';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { Job } from '../../../models/job';
import { LiveLogModalComponent } from '../../modals/live-log-modal/live-log-modal.component';
import { NotificationModalComponent } from '../../modals/notification-modal/notification-modal.component';
import { ActionType, ObjectActionListType, Permissions } from '../../../models/permission';
import { ChangeServiceModalComponent } from '../../modals/change-service-modal/change-service-modal.component';
import { Project } from '../../../models/project';
import { TaskStatus } from '../../../models/task';

export enum MenuItem {
    LIVELOG,
    NOTIFICATION
}

@Component({
    standalone: false,
    selector: 'res-job-management',
    templateUrl: './job-management.component.html',
    styleUrls: ['./job-management.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class JobManagementComponent implements OnDestroy, OnInit {
    @ViewChild(GenericGridComponent, { static: true }) grid: GenericGridComponent;
    modalClass = 'job-management';
    modalHelpIndex: HelpIndex;
    automationId = 'job-management';
    selectedJob: Job = null;
    allSelectedJobs = null;
    jobDetails: Job = null;
    rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    gridName = 'Job Management';

    isTaskDetailsBusy: boolean;
    taskDetailsBusyMessage: string;
    taskList: Array<any>;
    currentEmail: string;
    hasPermissions = false;
    isDirty = false;
    isBusy = false;
    isBusyMessage = 'Saving ...';
    services: Array<any>;
    projects: Array<any>;
    projectHandles = [];
    jobsHandles = [];
    private entityChangedEvent: Subscription;

    columnDefs = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            checkboxSelection: true,
            headerCheckboxSelection: true,
            automationId: this.automationId,
            minWidth: 50, maxWidth: 50, width: 50,
            rowDrag: true
        },
        {
            headerName: 'Process',
            automationId: this.automationId,
            lockVisible: true,
            field: 'name',
            minWidth: 90,
            width: 300,
            suppressSizeToFit: true,
            filterType: FilterTemplateType.freeform,
            cellRenderer: 'gridWorkbasketActionsCellRenderer',
            cellRendererParams: {
                permissionControl: this.permissionControl.bind(this)
            }
        },
        {
            headerName: 'Type',
            automationId: this.automationId,
            field: 'type',
            minWidth: 30,
            width: 100,
            suppressSizeToFit: true,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Username',
            automationId: this.automationId,
            field: 'userName',
            minWidth: 30,
            suppressSizeToFit: false,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Organization',
            automationId: this.automationId,
            minWidth: 30,
            suppressSizeToFit: false,
            field: 'organization',
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Project Name - Description',
            automationId: this.automationId,
            field: 'projectNameDescription',
            minWidth: 30,
            suppressSizeToFit: false,
            filterType: FilterTemplateType.freeform,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Service',
            automationId: this.automationId,
            field: 'serviceName',
            minWidth: 30,
            suppressSizeToFit: false,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            colId: 'rateEditableNumericInput',
            headerName: 'Rate (Requested / Current)',
            automationId: this.automationId,
            field: 'desiredRate',
            minWidth: 30,
            suppressSizeToFit: true,
            cellTemplateType: CellTemplateType.editableNumericInput,
        },
        {
            colId: 'editablePriorityButtonGrpInput',
            headerName: 'Priority',
            automationId: this.automationId,
            field: 'priority',
            width: 170, minWidth: 170, maxWidth: 170,
            cellTemplateType: CellTemplateType.jobPriorityTypeButtons
        },
        {
            headerName: 'Job State',
            automationId: this.automationId,
            field: 'currentFSMState',
            minWidth: 30,
            suppressSizeToFit: false,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Task Duration',
            automationId: this.automationId,
            field: 'processingDuration',
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            minWidth: 30,
            width: 100,
            suppressSizeToFit: true,
            cellTemplateType: CellTemplateType.computedDuration,
        },
        {
            headerName: 'Task %',
            automationId: this.automationId,
            field: 'percentComplete',
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            minWidth: 30,
            maxWidth: 65,
            width: 65,
            suppressSizeToFit: true,
            cellTemplateType: CellTemplateType.jobManagementComplete,
        },
        {
            headerName: 'Results',
            automationId: this.automationId,
            field: 'numberResults',
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            minWidth: 30,
            width: 75,
            suppressSizeToFit: true,
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true,
            hide: false
        },
    );

    updateStatusType = UpdateStatusType.TASK;
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.realmManager_listJobs,
        data: { filters: [{ attribute: 'DESCRIPTION', value: 'ConnectorActivation', operator: 'EQUALS' }], contextHandle: this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser?.importedFromSystemUser ? 'super_system_customer' : '' },
        responseName: 'jobs'
    });

    gridContext = new GridContext({
        uiMultiFilter: true,
        loadAndFilter: true
    });

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Create Job Notification ...', iconClass: 'fa-bell-o', iconColor: 'color-grey-10',
            click: (item) => this.contextMenuActions(MenuItem.NOTIFICATION, item),
            enabled: (item) => true,
            visible: (item) => true,
        }),
        new ContextMenuOption({
            name: 'View Live Log ...', iconClass: 'fa-info', iconColor: 'color-grey-10',
            click: (item) => this.contextMenuActions(MenuItem.LIVELOG, item),
            enabled: (item) => true,
            visible: (item) => true
        })
    ];

    constructor(
        private commonService: CommonService, private restService: RestService) {

        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.TASK) {
                this.grid.gridContext.loadAndFilter = true;
                this.commonService.suppressGridSpinnerIfPoller = true;
                this.reselectNodeAfterUpdate();
            }
        });
    }

    ngOnInit(): void {
        this.hasPermissions = Permissions.checkUserPermission(ObjectActionListType.JOB_MANAGEMENT, ActionType.CREATE);
        if (!this.hasPermissions) {
            this.columnDefs = this.columnDefs.filter(col => col.colId !== 'checkbox');
            this.columnDefs.find(col => col.colId === 'rateEditableNumericInput').cellTemplateType = CellTemplateType.jobManagementRate;
            this.columnDefs.find(col => col.colId === 'editablePriorityButtonGrpInput').cellTemplateType = CellTemplateType.basicLabelWithTooltip;
            this.rowSelection = GridRowSelectionType.single;
        }
        else {
            this.getListOfServices();
        }
    }

    permissionControl(action: string): boolean {
        if (action === 'Pause' || action === 'Resume' || action === 'Cancel') {
            return this.hasPermissions;
        }
        return false;
    }

    isApplyDiscardEnabled(): boolean {
        return this.isDirty || this.jobsHaveRateChanges();
    }

    jobsHaveRateChanges(): boolean {
        const listOfJobsWithChangedRate = this.grid.getData()?.filter(job => job.rateHasChanged);
        return listOfJobsWithChangedRate?.length > 0;
    }

    areRowsSelected(): boolean {
        return this.grid?.selectedRows?.length >= 1;
    }

    contextMenuActions(actionType: MenuItem, rowData: Job): void {
        switch (actionType) {
            case MenuItem.LIVELOG:
                this.commonService.openModal(LiveLogModalComponent, { modalData: { handle: rowData.handle }, class: 'vertical-percent-100 horizontal-percent-100' });
                break;
            case MenuItem.NOTIFICATION:
                this.openNotificationModal([rowData]);
                break;
        }

    }

    getListOfServices(): void {
        this.restService.post(Endpoint.realmManager_listProjects, { contextHandle: 'noHandle', filters: [], systemScope: true }).then((response) => {
            if (response) {
                this.services = response.services;
                this.projects = response.projects;
            }
        });
    }

    openNotificationModal(rowData: any[]): void {
        if (this.commonService.currentUser.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.type === 'System User') {
            this.restService.post(Endpoint.userManager_getOrgUserbyName, {
                handle: this.commonService.currentUser.userName,
                orgName: 'super_system_customer',
                systemScope: true
            }).then(response => {
                if (response) {
                    this.currentEmail = response.user.email;
                    this.commonService.openModal(NotificationModalComponent, {
                        modalData: { item: rowData, modalType: 'job-notification', currentEmail: this.currentEmail }
                    });
                }
            });
        }
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
        if (rowNode?.data && action) {
            switch (action) {
                case CellActionType.workbasketPause:
                    this.pause(rowNode.data);
                    break;
                case CellActionType.workbasketResume:
                    this.resume(rowNode.data);
                    break;
                case CellActionType.workbasketCancel:
                    this.cancel(rowNode.data);
                    break;
                case CellActionType.workbasketIcon:
                    this.iconClick(rowNode.data);
                    break;
                case CellActionType.lowPriorityJob:
                    this.updateTaskPriority('LOW', rowNode.data.handle);
                    break;
                case CellActionType.mediumPriorityJob:
                    this.updateTaskPriority('NORMAL', rowNode.data.handle);
                    break;
                case CellActionType.highPriorityJob:
                    this.updateTaskPriority('HIGH', rowNode.data.handle);
                    break;
            }
        }
    }

    updateTaskPriority(priorityType: string, taskHandle: string) {
        // prevent updating the task priority when there are unsaved changes
        if (this.isApplyDiscardEnabled()) {
            this.commonService.openInformationToastNotification('There are unsaved changes to Job Order and/or Rate. Please apply or discard these changes before changing Priority.');
            return;
        }
        // api call to update the priority of a task
        this.restService.post(Endpoint.taskManager_updateJobPriority, {
            priority: priorityType,
            taskHandle: taskHandle
        }).then((response) => {
            this.update();
        });
    }

    pause(task: Job): void {
        if (task?.handle) {
            this.restService.post(Endpoint.taskManager_pauseTask, { taskHandle: task.handle, systemScope: true }).then(() => {
                this.hideBulkSearchBusySpinner(task);
                this.update();
            });
        }
    }

    resume(task: Job): void {
        if (task?.handle) {
            this.restService.post(Endpoint.taskManager_resumeTask, { taskHandle: task.handle, systemScope: true }).then(() => {
                this.update();
            });
        }
    }

    cancel(task: Job): void {
        if (task?.handle) {
            this.commonService.openConfirmationModal('Cancel "' + (task as Job).description.replace(/&lt;/g, '<').replace(/&gt;/g, '>') + '"?', 'Cancel Job', 'Cancel Job', `Don't Cancel Job`).then(result => {
                if (result.modalResult) {
                    this.restService.post(Endpoint.taskManager_cancelTask, { taskHandle: task.handle, systemScope: true }).then(() => {
                        this.hideBulkSearchBusySpinner(task);
                        this.update();
                    });
                }
            });
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent): void => {
        if (event && event.api && (event?.source === 'checkboxSelected' || event?.source === 'rowClicked')) {
            this.allSelectedJobs = event.api.getSelectedRows();
            const nrOfSelectedRows = this.allSelectedJobs?.length;
            this.selectedJob = nrOfSelectedRows === 1 ? this.allSelectedJobs[0] : null;

            this.taskDetailsBusyMessage = 'Loading ...';
            this.isTaskDetailsBusy = true;
            if (this.selectedJob) {
                this.restService.post(Endpoint.taskManager_getTasks, {
                    taskHandles: [this.selectedJob.handle],
                    includeDrDebug: true,
                    includeResourceStatistics: true,
                    systemScope: true
                }).then(response => {
                    this.isTaskDetailsBusy = false;
                    if (response.tasks && response.tasks.length > 0) {
                        this.taskList = response.tasks[0].currentStatus;
                        this.jobDetails = response.tasks[0].currentStatus;
                    }
                    this.grid.refreshColumns();
                }).catch(error => {
                    this.isTaskDetailsBusy = false;
                });
            }
            setTimeout(() => {
                this.grid.refreshColumns();
            });
        }
    }

    responseCallback = (response: any) => {
        const jobs = new Array<Job>();
        response.jobs.forEach(element => {
            const job = new Job(element);
            job.totalCores = response.totalCores;
            if (!job.originalDesiredRate) {
                job.originalDesiredRate = element.desiredRate;
            }
            jobs.push(job);
        });
        response.jobs = jobs;
        this.jobsHandles = jobs?.length > 0 ? jobs.map(job => job.handle) : [];
    }

    iconClick(task: Job): void {
        if (task.status === TaskStatus.PAUSED_ON_FAILURE) {
            const error = task.extendedStatus ? task.extendedStatus : 'Error information is unavailable.';
            this.commonService.openErrorModal(error, 'Task Failed', false);
        }
    }

    update(): void {
        this.grid.gridContext.loadAndFilter = true;
        this.grid.loadData();
        this.grid.gridContext.loadAndFilter = false;
        this.reselectNodeAfterUpdate();
        this.isDirty = false;
    }

    apply(): void {
        this.updatePriorityOrder();
    }

    cancelJobs(): void {
        if (this.areRowsSelected()) {
            const selectedJobsInfo = this.grid.selectedRows?.map(job => {
                return {
                    handle: job.handle,
                    prjName: job.projectName,
                    orgName: job.organization,
                    type: job.type,
                    taskStatus: job.taskStatus
                }
            });
            const noOfSelectedJobs = selectedJobsInfo?.length;

            if (noOfSelectedJobs > 0) {
                if (noOfSelectedJobs === 1 && this.selectedJob) {
                    this.cancel(this.selectedJob);
                } else {
                    this.commonService.openConfirmationModal(`Cancel the ${noOfSelectedJobs} Selected Jobs ?`, 'Cancel Multiple Jobs', 'Cancel Jobs', `Don't Cancel Jobs`).then(result => {
                        if (result.modalResult) {
                            selectedJobsInfo.forEach(jobInfo => {
                                if (jobInfo.handle) {
                                    this.restService.post(Endpoint.taskManager_cancelTask, { taskHandle: jobInfo.handle, systemScope: true }).then(() => {
                                        this.hideBulkSearchBusySpinner(jobInfo);
                                    });
                                }
                            });
                        }
                    });
                }
            }
        }
    }

    hideBulkSearchBusySpinner(task: any) {
        if (task.type === 'VALIDATE_BULK_SEARCH' && task.taskStatus === 'IN_PROGRESS' && this.commonService.isExternalApp) {
            window.opener.hideBusySpinner();
        }
    }

    getProjectHandleForJob(jobProjName: string, jobOrgName: string): string {
        const jobProject = this.projects.find(prj => prj.name === jobProjName && prj.orgName === jobOrgName);
        return jobProject ? jobProject.handle : null;
    }

    discardChanges(): void {
        this.isDirty = false;
        if (this.grid) {
            this.grid.loadData();
        }
    }

    createJobNotification(): void {
        if (this.grid) {
            this.openNotificationModal(this.grid.selectedRows);
        }
    }

    changePrjService(): void {
        if (this.areRowsSelected() && this.projects?.length > 0) {
            this.projectHandles = [];
            let projectOfSelectedJob = null;
            const projectInfoFromJobs = this.grid.selectedRows?.map(r => { return { prjName: r.projectName, orgName: r.organization, serviceName: r.serviceName } });
            if (projectInfoFromJobs) {
                if (projectInfoFromJobs.length === 1) {
                    projectOfSelectedJob = this.projects.find(prj => prj.name === projectInfoFromJobs[0].prjName && prj.orgName === projectInfoFromJobs[0].orgName);
                } else {
                    projectInfoFromJobs.forEach(p => {
                        const prjHandle = this.getProjectHandleForJob(p.prjName, p.orgName);
                        if (prjHandle) {
                            this.projectHandles.push(prjHandle);
                        }
                    })
                }
            }
            const allJobsHaveSameService = projectInfoFromJobs?.length === projectInfoFromJobs?.filter(j => j.serviceName === projectInfoFromJobs[0].serviceName).length;
            const serviceToSelect = (projectOfSelectedJob || allJobsHaveSameService) ? projectInfoFromJobs[0].serviceName : null;
            this.commonService.openModal(ChangeServiceModalComponent, {
                modalData: {
                    project: projectOfSelectedJob ? new Project(projectOfSelectedJob) : null,
                    projectHandles: this.projectHandles?.length > 0 ? this.projectHandles : null,
                    services: this.services,
                    currentService: serviceToSelect
                },
                class: 'vertical-px-500 horizontal-px-1200'
            });
        }
    }

    updatePriorityOrder(): void {
        if (this.grid) {
            this.isBusy = true;
            const requestList = this.grid.getData().map(job => { return { desiredRate: job.desiredRate, taskHandle: job.handle } });
            this.restService.post(Endpoint.taskManager_applyBulkJobPriorityUpdate, { updatedPriorityOrder: requestList, systemScope: true }).then(() => {
                this.isDirty = false;
                if (this.jobsHaveRateChanges()) {
                    this.grid.getData().forEach(job => job.rateHasChanged = false);
                }
                this.isBusy = false;
            }, () => { this.isBusy = false; });
        }
    }

    reselectNodeAfterUpdate(): void {
        setTimeout(() => {
            if (this.allSelectedJobs?.length > 1) {
                const indexesToRemove = [];
                this.allSelectedJobs.forEach((job, idx: number) => {
                    setTimeout(() => {
                        const item = this.grid.findNode((node: RowNode) => node.data.handle === job.handle, true);
                        if (!item) {
                            indexesToRemove.push(idx);
                        }
                    }, 400);
                });
                if (indexesToRemove.length > 0) {
                    indexesToRemove.forEach(idx => this.allSelectedJobs.splice(idx, 1));
                }
            } else {
                const item = this.grid.findNode((node: RowNode) => node.data.handle === this.selectedJob?.handle, true);
                if (!item) {
                    this.selectedJob = null;
                }
            }
        }, 400);
    }

    openHelp = (): void => this.commonService.openHelp(HelpIndex.sys_job_mgmt_dlg);

    onRowDragEnd(event: RowDragEvent): void {
        if (event && this.jobsOrderChanged()) {
            this.isDirty = true;
        } else {
            this.isDirty = false;
        }
    }

    jobsOrderChanged(): boolean {
        const gridList = this.grid.getData();
        if (gridList?.length === this.jobsHandles.length) {
            for (let i = 0; i < this.jobsHandles.length; i++) {
                if (this.jobsHandles[i] !== gridList[i]?.handle) {
                    return true;
                }
            }
        }
        return false;
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
    }
}
